import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { 
  Plus, 
  CheckCircle2, 
  Circle, 
  Clock, 
  User, 
  AlertCircle,
  ExternalLink,
  ListTodo
} from 'lucide-react';
import apiService from '../../services/apiService';
import AddActionModal from './AddActionModal';

// Priority color mapping
const priorityColors = {
  High: 'bg-red-100 text-red-800 border-red-200',
  Medium: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  Low: 'bg-green-100 text-green-800 border-green-200'
};

// Status color mapping
const statusColors = {
  Open: 'text-orange-600',
  'In Progress': 'text-blue-600',
  Done: 'text-green-600'
};

/**
 * ActionStatsHeader Component
 * Displays summary metrics for actions
 */
const ActionStatsHeader = ({ actions }) => {
  const total = actions.length;
  const completed = actions.filter(a => a.status === 'Done').length;
  const open = actions.filter(a => a.status === 'Open').length;
  const highPriority = actions.filter(a => a.priority === 'High').length;
  const completionRate = total > 0 ? Math.round((completed / total) * 100) : 0;

  return (
    <div className="grid grid-cols-4 gap-4 mb-6">
      <Card className="bg-gradient-to-br from-blue-50 to-blue-100">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 font-medium">Total Actions</p>
              <p className="text-3xl font-bold text-blue-900">{total}</p>
            </div>
            <ListTodo className="w-8 h-8 text-blue-600" />
          </div>
        </CardContent>
      </Card>

      <Card className="bg-gradient-to-br from-orange-50 to-orange-100">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 font-medium">Open</p>
              <p className="text-3xl font-bold text-orange-900">{open}</p>
            </div>
            <Circle className="w-8 h-8 text-orange-600" />
          </div>
        </CardContent>
      </Card>

      <Card className="bg-gradient-to-br from-green-50 to-green-100">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 font-medium">Completed</p>
              <p className="text-3xl font-bold text-green-900">{completed}</p>
            </div>
            <CheckCircle2 className="w-8 h-8 text-green-600" />
          </div>
        </CardContent>
      </Card>

      <Card className="bg-gradient-to-br from-red-50 to-red-100">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 font-medium">High Priority</p>
              <p className="text-3xl font-bold text-red-900">{highPriority}</p>
            </div>
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

/**
 * ProgressTracker Component
 * Visual progress bar showing completion percentage
 */
const ProgressTracker = ({ completed, total }) => {
  const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;

  return (
    <div className="mb-6">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-gray-700">
          Overall Progress
        </span>
        <span className="text-sm font-semibold text-gray-900">
          {completed} of {total} completed ({percentage}%)
        </span>
      </div>
      <Progress value={percentage} className="h-3" />
    </div>
  );
};

/**
 * UseCaseActionRow Component
 * Individual action display with checkbox toggle
 */
const UseCaseActionRow = ({ action, onToggle }) => {
  const [isToggling, setIsToggling] = useState(false);

  const handleToggle = async () => {
    setIsToggling(true);
    try {
      await onToggle(action);
    } finally {
      setIsToggling(false);
    }
  };

  const isDone = action.status === 'Done';

  return (
    <div className="flex items-start gap-4 p-4 bg-white border border-gray-200 rounded-lg hover:shadow-md transition-shadow">
      {/* Checkbox */}
      <button
        onClick={handleToggle}
        disabled={isToggling}
        className={`mt-1 flex-shrink-0 ${isToggling ? 'opacity-50 cursor-wait' : 'cursor-pointer'}`}
        aria-label={isDone ? 'Mark as incomplete' : 'Mark as complete'}
      >
        {isDone ? (
          <CheckCircle2 className="w-5 h-5 text-green-600" />
        ) : (
          <Circle className="w-5 h-5 text-gray-400 hover:text-gray-600" />
        )}
      </button>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-3 mb-2">
          <h4 className={`font-semibold text-gray-900 ${isDone ? 'line-through text-gray-500' : ''}`}>
            {action.label}
          </h4>
          <Badge className={`${priorityColors[action.priority]} text-xs px-2 py-1 flex-shrink-0`}>
            {action.priority}
          </Badge>
        </div>

        {action.description && (
          <p className={`text-sm text-gray-600 mb-3 ${isDone ? 'line-through' : ''}`}>
            {action.description}
          </p>
        )}

        <div className="flex items-center gap-4 text-xs text-gray-500">
          {action.role && (
            <div className="flex items-center gap-1">
              <User className="w-3.5 h-3.5" />
              <span>{action.role}</span>
            </div>
          )}
          {action.due_window && (
            <div className="flex items-center gap-1">
              <Clock className="w-3.5 h-3.5" />
              <span>{action.due_window}</span>
            </div>
          )}
          <div className={`flex items-center gap-1 font-medium ${statusColors[action.status]}`}>
            <span>{action.status}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Main UseCaseActionsTab Component
 */
const UseCaseActionsTab = ({ clientId, useCaseId, profileData }) => {
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [error, setError] = useState(null);

  // Fetch actions on mount
  useEffect(() => {
    fetchActions();
  }, [clientId, useCaseId]);

  const fetchActions = async () => {
    setLoading(true);
    setError(null);
    try {
      console.log('🔍 Fetching actions - clientId:', clientId, 'useCaseId:', useCaseId);

      const response = await apiService.getActions(clientId, useCaseId);

      const actionsData = response.actions || response.data || response;
      setActions(Array.isArray(actionsData) ? actionsData : []);

      console.log('✅ Loaded actions:', actionsData.length);
    } catch (err) {
      console.error('Failed to fetch actions:', err);
      setError('Failed to load actions. Please try again.');
      toast.error('Failed to load actions');
      setActions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (action) => {
    const newStatus = action.status === 'Done' ? 'Open' : 'Done';
    const previousStatus = action.status;

    // Optimistic update
    setActions(prevActions =>
      prevActions.map(a =>
        a.action_id === action.action_id
          ? { ...a, status: newStatus }
          : a
      )
    );

    try {
      await apiService.updateAction(action.action_id, { 
        client_id: clientId,
        status: newStatus 
      });

      // Show success toast
      if (newStatus === 'Done') {
        toast.success('Action marked as complete! 🎉');
      } else {
        toast.success('Action reopened');
      }
    } catch (err) {
      console.error('Failed to update action:', err);
      
      // Rollback on failure
      setActions(prevActions =>
        prevActions.map(a =>
          a.action_id === action.action_id
            ? { ...a, status: previousStatus }
            : a
        )
      );

      toast.error('Failed to update action status');
    }
  };

  const handleAddAction = async (newAction) => {
    try {
      const createdAction = await apiService.createAction({
        client_id: clientId,
        use_case_id: useCaseId,
        ...newAction,
        source: 'user'
      });
      
      // Show success toast
      toast.success('Action created successfully! ✅');
      
      // Refresh actions list
      await fetchActions();
      setIsModalOpen(false);
    } catch (err) {
      console.error('Failed to create action:', err);
      toast.error('Failed to create action');
      throw err;
    }
  };

  // Group actions by priority
  const groupedActions = {
    High: Array.isArray(actions) ? actions.filter(a => a.priority === 'High') : [],
    Medium: Array.isArray(actions) ? actions.filter(a => a.priority === 'Medium') : [],
    Low: Array.isArray(actions) ? actions.filter(a => a.priority === 'Low') : []
  };

  const completedCount = Array.isArray(actions) ? actions.filter(a => a.status === 'Done').length : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading actions...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Card className="shadow-md">
        <CardContent className="p-6">
          <div className="text-center py-8">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
            <p className="text-red-600 font-medium">{error}</p>
            <Button onClick={fetchActions} className="mt-4">
              Retry
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div>
      {/* Header with Add Action Button */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Actions</h2>
          <p className="text-sm text-gray-600 mt-1">
            Track and manage implementation tasks for {profileData?.title || 'this use case'}
          </p>
        </div>
        <Button
          onClick={() => setIsModalOpen(true)}
          className="bg-orange-500 hover:bg-orange-600 text-white"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Action
        </Button>
      </div>

      {/* Stats Header */}
      <ActionStatsHeader actions={actions} />

      {/* Progress Tracker */}
      <ProgressTracker completed={completedCount} total={actions.length} />

      {/* Actions List */}
      {actions.length === 0 ? (
        <Card className="shadow-md">
          <CardContent className="p-12 text-center">
            <ListTodo className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-700 mb-2">No actions yet</h3>
            <p className="text-gray-500 mb-6">
              Get started by adding your first action item for this use case.
            </p>
            <Button
              onClick={() => setIsModalOpen(true)}
              className="bg-orange-500 hover:bg-orange-600 text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add First Action
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* High Priority Actions */}
          {groupedActions.High.length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-red-600" />
                High Priority ({groupedActions.High.length})
              </h3>
              <div className="space-y-3">
                {groupedActions.High.map(action => (
                  <UseCaseActionRow
                    key={action.action_id}
                    action={action}
                    onToggle={handleToggleStatus}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Medium Priority Actions */}
          {groupedActions.Medium.length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                Medium Priority ({groupedActions.Medium.length})
              </h3>
              <div className="space-y-3">
                {groupedActions.Medium.map(action => (
                  <UseCaseActionRow
                    key={action.action_id}
                    action={action}
                    onToggle={handleToggleStatus}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Low Priority Actions */}
          {groupedActions.Low.length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                Low Priority ({groupedActions.Low.length})
              </h3>
              <div className="space-y-3">
                {groupedActions.Low.map(action => (
                  <UseCaseActionRow
                    key={action.action_id}
                    action={action}
                    onToggle={handleToggleStatus}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Link to Action Board */}
          <Card className="shadow-md mt-6 bg-gradient-to-r from-blue-50 to-indigo-50">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">
                    View All Actions Across Use Cases
                  </h3>
                  <p className="text-sm text-gray-600">
                    See actions from all use cases in the comprehensive Action Board
                  </p>
                </div>
                <Button
                  onClick={() => window.location.href = `/action-board/${clientId}`}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  View All Actions
                  <ExternalLink className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Add Action Modal */}
      <AddActionModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleAddAction}
        useCaseTitle={profileData?.title}
      />
    </div>
  );
};

export default UseCaseActionsTab;